/**
 * Sticky Live Price Calculator Component
 * 
 * A sticky version of the live price calculator that follows the user
 * as they scroll through options, ensuring they always see pricing updates
 * in real-time without needing to scroll.
 * 
 * @author Wildlife Project Team
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, TrendingUp, ArrowRight, Eye, EyeOff, ChevronUp, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type CalculationResult } from '../hooks/pricing-types';
import { getBasePriceTypeLabel } from '../hooks/basePricingCalculator';

interface StickyLivePriceCalculatorProps {
  calculation: CalculationResult;
  basePriceType: 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
  onShowExamples: () => void;
  className?: string;
  /** 
   * Disables the sticky positioning behavior. 
   * Use this when the component is already inside a sticky container
   * to prevent positioning conflicts.
   * @default false
   */
  disableSticky?: boolean;
}

export function StickyLivePriceCalculator({
  calculation,
  basePriceType,
  onShowExamples,
  className = "",
  disableSticky = false
}: StickyLivePriceCalculatorProps) {
  const { breakdown, totalPrice, currencySymbol, scaledBasePrice, basePrice } = calculation;
  const [isSticky, setIsSticky] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [lastPrice, setLastPrice] = useState(totalPrice);
  const [priceChanged, setPriceChanged] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);

  // Track scroll position to determine when to make sticky
  // Skip sticky logic entirely if disabled
  useEffect(() => {
    if (disableSticky) {
      setIsSticky(false); // Ensure sticky is always false when disabled
      return;
    }
    
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const shouldBeSticky = scrollY > 200; // Make sticky after 200px scroll
      setIsSticky(shouldBeSticky);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [disableSticky]);

  // Animate price changes - IMPROVED to trigger every time
  useEffect(() => {
    if (totalPrice !== lastPrice) {
      setPriceChanged(true);
      setLastPrice(totalPrice);
      setAnimationKey(prev => prev + 1); // Force re-animation
      
      // Reset animation after duration
      const timer = setTimeout(() => {
        setPriceChanged(false);
      }, 600);
      
      return () => clearTimeout(timer);
    }
  }, [totalPrice, lastPrice]);

  const stickyVariants = {
    fixed: {
      position: 'static' as const, // Let parent container handle positioning
      width: '100%',
      zIndex: 1,
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      scale: 1
    },
    static: {
      position: 'static' as const,
      width: '100%',
      zIndex: 1,
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      scale: 1
    }
  };

  // FIXED: White colors for visibility on green background
  const priceVariants = {
    normal: {
      scale: 1,
      color: '#ffffff', // White for visibility
      textShadow: 'none'
    },
    changed: {
      scale: [1, 1.15, 1],
      color: ['#ffffff', '#f0f9ff', '#ffffff'], // White to light blue back to white
      textShadow: [
        'none',
        '0 0 10px rgba(240, 249, 255, 0.8), 0 0 20px rgba(240, 249, 255, 0.4)',
        'none'
      ],
      transition: {
        duration: 0.6,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <motion.div
      variants={stickyVariants}
      animate="fixed" // Always use the enhanced shadow/styling
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`}
      style={{ 
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)'
      }}
    >
      {/* Sticky Header - Always Visible */}
      <div className="bg-gradient-to-r from-primary to-green-600 text-white p-3 lg:p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 lg:w-5 lg:h-5" />
            <h5 className="font-semibold text-sm lg:text-base">Live Price</h5>
            <Badge variant="secondary" className="text-xs bg-white/20 text-white border-white/30 hidden sm:inline-flex">
              Real-time
            </Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-6 w-6 p-0 text-white hover:bg-white/20"
            >
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </Button>
          </div>
        </div>

        {/* Total Price - Always Visible with FIXED Animation */}
        <div className="mt-2 lg:mt-2">
          <motion.div
            key={animationKey} // Force re-animation on every price change
            variants={priceVariants}
            animate={priceChanged ? 'changed' : 'normal'}
            className="text-xl lg:text-2xl font-bold"
            style={{ color: '#ffffff' }} // Ensure white color
          >
            {currencySymbol}{totalPrice.toFixed(2)}
          </motion.div>
          <div className="text-xs text-white/80">
            {breakdown.length - 1} option{breakdown.length - 1 !== 1 ? 's' : ''} selected
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ 
            height: isExpanded ? 'auto' : 0, 
            opacity: isExpanded ? 1 : 0 
          }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
            <div className="p-3 lg:p-4 space-y-2 lg:space-y-3">
              {/* Price Breakdown */}
              <div className="space-y-2">
                {breakdown.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex justify-between items-center text-xs lg:text-sm py-1"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-gray-700 truncate text-xs lg:text-sm">{item.label}</div>
                      <div className="text-xs text-gray-500 truncate hidden lg:block">{item.description}</div>
                    </div>
                    <div className="text-right ml-2">
                      <div className="font-medium text-gray-900 text-xs lg:text-sm">
                        {currencySymbol}{item.amount.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500 capitalize hidden lg:block">
                        {item.type === 'choice' ? 'Option' : 
                         item.type === 'base' ? 'Base' : 
                         item.type === 'option' ? 'Add-on' : item.type}
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* Show when no options are selected */}
                {breakdown.length === 1 && breakdown[0].type === 'base' && (
                  <div className="text-center py-2 text-gray-500 text-xs italic">
                    Select options above to see pricing updates
                  </div>
                )}
              </div>

              {/* Summary Stats */}
              <div className="border-t pt-2 lg:pt-3">
                <div className="grid grid-cols-2 gap-2 lg:gap-3 text-xs">
                  <div className="text-center">
                    <div className="font-bold text-xs lg:text-sm text-primary">
                      {currencySymbol}{scaledBasePrice.toFixed(2)}
                    </div>
                    <div className="text-gray-500 text-xs">Base Price</div>
                    <div className="text-gray-400 text-xs hidden lg:block">
                      {getBasePriceTypeLabel(basePriceType)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-xs lg:text-sm text-blue-600">
                      {breakdown.length - 1}
                    </div>
                    <div className="text-gray-500 text-xs">Add-ons</div>
                    <div className="text-gray-400 text-xs hidden lg:block">
                      {breakdown.filter(item => item.type !== 'base').length > 0 ? 'Active' : 'None'}
                    </div>
                  </div>
                </div>

                {/* Progress Bar - Hidden on small mobile */}
                <div className="mt-2 lg:mt-3 hidden sm:block">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <TrendingUp className="w-3 h-3" />
                    <div className="flex-1 bg-gray-200 rounded-full h-1">
                      <div 
                        className="bg-primary h-1 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min(100, (scaledBasePrice / totalPrice) * 100)}%` 
                        }}
                      />
                    </div>
                    <span>
                      {Math.round((scaledBasePrice / totalPrice) * 100)}% base
                    </span>
                  </div>
                </div>

                {/* Examples Button */}
                <button
                  onClick={onShowExamples}
                  className="w-full mt-2 lg:mt-3 text-xs text-primary hover:text-[#1e4a20] transition-colors flex items-center justify-center gap-1 py-1.5 lg:py-2 border border-primary/20 rounded hover:bg-primary/5"
                >
                  <span className="hidden sm:inline">See Examples</span>
                  <span className="sm:hidden">Examples</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}